import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import { Loader2, ShieldCheck, KeyRound, Copy, Check, ShieldAlert } from "lucide-react";
import { SupportContactStrip } from "./SupportContactStrip";
import { toast } from "sonner";

export function TwoFactorSettings({
    apiPrefix = "/auth/2fa",
    required = false,
    allowDisable = true,
    onEnabled,
}) {
    const { user, refreshMe } = useAuth();
    const navigate = useNavigate();
    const [setupState, setSetupState] = useState(null);
    const [code, setCode] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const [copied, setCopied] = useState(false);

    const startSetup = async () => {
        try {
            setBusy(true);
            const { data } = await api.post(`${apiPrefix}/setup`);
            setSetupState(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message);
        } finally {
            setBusy(false);
        }
    };

    const enable = async () => {
        try {
            setBusy(true);
            const { data } = await api.post(`${apiPrefix}/enable`, { code });
            toast.success("Two-factor authentication enabled");
            setSetupState(null);
            setCode("");
            await refreshMe();
            onEnabled?.(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message);
        } finally {
            setBusy(false);
        }
    };

    const disable = async () => {
        try {
            setBusy(true);
            await api.post(`${apiPrefix}/disable`, { code, password });
            toast.success("Two-factor authentication disabled");
            setCode("");
            setPassword("");
            await refreshMe();
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message);
        } finally {
            setBusy(false);
        }
    };

    const copySecret = async () => {
        await navigator.clipboard.writeText(setupState.secret);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    if (user?.totp_enabled) {
        return (
            <div className="surface p-5" data-testid="twofa-enabled">
                <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-md bg-[#00BF0D]/8 border border-[#00BF0D]/15 flex items-center justify-center">
                        <ShieldCheck className="h-5 w-5 text-[#00BF0D]" />
                    </div>
                    <div className="flex-1">
                        <h3 className="font-heading text-lg">Two-factor authentication</h3>
                        <p className="text-xs text-white/55 mt-1">
                            Active. You&apos;ll be asked for a 6-digit code each time you sign in.
                        </p>
                    </div>
                    <span className="fs-pill shrink-0 gap-1 rounded-full bg-[#00BF0D]/8 border border-[#00BF0D]/20 px-2 py-0.5 text-[10px] font-semibold text-[#00BF0D]">
                        <Check className="h-3 w-3" /> Enabled
                    </span>
                </div>
                {allowDisable ? (
                    <div className="mt-5 space-y-3 max-w-md">
                        <div>
                            <label className="label-eyebrow">Account password</label>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="input-dark mt-1.5 w-full rounded-md px-3 py-2 text-sm"
                                data-testid="twofa-disable-password"
                            />
                        </div>
                        <div>
                            <label className="label-eyebrow">Current 6-digit code</label>
                            <input
                                value={code}
                                onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ""))}
                                maxLength={6}
                                className="input-dark mt-1.5 w-32 rounded-md px-3 py-2 text-sm font-mono tracking-widest"
                                data-testid="twofa-disable-code"
                            />
                        </div>
                        <button
                            onClick={disable}
                            disabled={busy || !code || !password}
                            className="btn-ghost rounded-md px-3 py-1.5 text-xs inline-flex items-center gap-2 hover:bg-rose-500/10 hover:text-rose-300"
                            data-testid="twofa-disable"
                        >
                            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                            Disable 2FA
                        </button>
                    </div>
                ) : (
                    <SupportContactStrip variant="security" className="mt-4 max-w-lg" />
                )}
                {required && (
                    <button
                        type="button"
                        onClick={() => navigate("/admin/dashboard")}
                        className="btn-neon mt-5 rounded-md px-4 py-2 text-sm"
                        data-testid="twofa-continue-admin"
                    >
                        Continue to admin console
                    </button>
                )}
            </div>
        );
    }

    return (
        <div className="surface p-5" data-testid="twofa-setup">
            {required && (
                <div className="mb-5 flex items-start gap-3 rounded-md border border-amber-500/25 bg-amber-500/10 px-4 py-3">
                    <ShieldAlert className="h-5 w-5 shrink-0 text-amber-300 mt-0.5" />
                    <div>
                        <p className="text-sm font-medium text-amber-100">Two-factor authentication is required</p>
                        <p className="mt-1 text-xs text-amber-200/75">
                            Set up an authenticator app below before you can use the rest of the super admin console.
                        </p>
                    </div>
                </div>
            )}
            <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-md bg-white/[0.04] border border-white/10 flex items-center justify-center">
                    <KeyRound className="h-5 w-5 text-white/55" />
                </div>
                <div className="flex-1">
                    <h3 className="font-heading text-lg">Two-factor authentication</h3>
                    <p className="text-xs text-white/55 mt-1">
                        Add an extra layer of security with any TOTP authenticator app (Google Authenticator, Microsoft Authenticator, Authy, 1Password, etc.).
                    </p>
                </div>
                {!required && (
                    <span className="fs-pill shrink-0 gap-1 rounded-full bg-amber-500/10 border border-amber-500/20 px-2 py-0.5 text-[10px] font-semibold text-amber-300">
                        Recommended
                    </span>
                )}
            </div>

            {!setupState ? (
                <button
                    onClick={startSetup}
                    disabled={busy}
                    className="btn-neon mt-5 rounded-md px-4 py-2 text-sm inline-flex items-center gap-2"
                    data-testid="twofa-start"
                >
                    {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                    {required ? "Set up 2FA" : "Enable 2FA"}
                </button>
            ) : (
                <div className="mt-5 grid sm:grid-cols-2 gap-5">
                    <div className="space-y-3">
                        <p className="text-xs text-white/55">1. Scan this QR code with your authenticator app.</p>
                        <div className="rounded-lg bg-white p-2 inline-block">
                            <img src={setupState.qr_data_url} alt="2FA QR" className="h-44 w-44" data-testid="twofa-qr-image" />
                        </div>
                        <div>
                            <p className="text-[11px] text-white/45 mb-1">Or enter this secret manually:</p>
                            <div className="rounded-md border border-white/[0.08] bg-[#1A1A1A] p-2 flex items-center gap-2">
                                <code className="text-xs font-mono text-white/80 flex-1 truncate" data-testid="twofa-secret">
                                    {setupState.secret}
                                </code>
                                <button onClick={copySecret} className="btn-ghost rounded-md px-2 py-1 text-xs inline-flex items-center gap-1">
                                    {copied ? <Check className="h-3 w-3 text-[#00BF0D]" /> : <Copy className="h-3 w-3" />}
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="space-y-3">
                        <p className="text-xs text-white/55">2. Enter the 6-digit code generated by your app to confirm setup.</p>
                        <input
                            value={code}
                            onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ""))}
                            maxLength={6}
                            placeholder="• • • • • •"
                            className="input-dark w-full rounded-md px-3 py-3 text-center text-xl font-mono tracking-[0.4em]"
                            data-testid="twofa-enable-code"
                        />
                        <button
                            onClick={enable}
                            disabled={busy || code.length < 6}
                            className="btn-neon w-full rounded-md py-2 text-sm inline-flex items-center justify-center gap-2"
                            data-testid="twofa-enable"
                        >
                            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                            Verify & enable
                        </button>
                        <button
                            onClick={() => {
                                setSetupState(null);
                                setCode("");
                            }}
                            className="btn-ghost w-full rounded-md py-2 text-xs"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
